import { platformAuthenticatorIsAvailable, startAuthentication, startRegistration } from "@simplewebauthn/browser";
import {
    PublicKeyCredentialCreationOptionsJSON,
    PublicKeyCredentialRequestOptionsJSON
} from "@simplewebauthn/typescript-types";
import _ from "lodash";
import { call, put, select, takeEvery } from 'redux-saga/effects';
import Cookies from "universal-cookie";
import { addAbTestToIsUsedQueueAction, appLog, logAppsflyerAction } from "../app/appActions";
import { LOG_AF_EVENT_NAMES, LOG_EVENT_TYPES } from "../app/appConstants";
import appHistory, { appHistoryPushWithSearchParams } from "../app/appHistory";
import { appInitStartSaga } from "../app/appSagas";
import { failurePut } from "../app/failurePut";
import { getRequest, postRequest } from "../app/request";
import {
    completeMobileLoginStateAction,
    registrationFailure,
    registrationSuccess,
    setChangeEmailSuccess,
    setRegistrationDataAction
} from "../modules/Login/loginActions";
import { updateCookiesOnAuthSaga, validateRegisterDataSaga } from "../modules/Login/loginSagas";
import { IRegistation } from "../modules/Login/types/loginActions";
import { MODALS_LOG_ACTION_TYPES } from "../modules/Modals/Modals";
import { isEmailValid } from "../utils/escapeUtils";
import {
    authRegisterMobileAppFailure,
    authRegisterMobileAppSuccess,
    authVerifyMobileAppFailure,
    authVerifyMobileAppSuccess,
    registrationStartMobileAction,
} from "./mobileAppActions";
import {
    ADD_MOBILE_SLIDER_PAGE,
    AUTH_REGISTER_MOBILE_APP_ACTION,
    AUTH_VERIFY_MOBILE_APP_ACTION,
    CLOSE_MOBILE_SLIDER_PAGE,
    OPEN_MOBILE_SLIDER_PAGE,
    REGISTRATION_PREPARE_MOBILE_ACTION,
    REGISTRATION_START_MOBILE_ACTION,
} from "./mobileAppConstants";
import { getAbTestsSelector } from "../app/appSelectors";
import { TAbTestNames } from "../app/types/appActions.t";
import updateURLSearchParams from "../utils/updateURLSearchParams";
import { pushGtmEventAction } from "../modules/GoogleTagManager/googleTagManagerActions";
import { GTM_EVENTS } from "../modules/GoogleTagManager/googleTagManagerConstants";
import { MOBILE_LOGIN_STATES } from "../modules/Login/loginConstants";

const browserCookies = new Cookies();


interface IOpenMobileSliderPageSaga {
    payload: {
        name?: string;
    } | string;
}

/**
 * @deprecated use openLocationSaga
 */
function* openMobileSliderPageSaga({ payload }: IOpenMobileSliderPageSaga) {
    const { name, ...searchParams } = _.isObject(payload) ? payload : { name: payload };
    appHistory.push(`${appHistory.location.pathname}?${updateURLSearchParams(new URLSearchParams(), {
        s: name,
        ...searchParams,
    }).toString()}`)
    yield put(appLog({
        type: LOG_EVENT_TYPES.page,
        payload: {
            action: MODALS_LOG_ACTION_TYPES.OPEN,
            name,
            ...searchParams,
        }
    }));
}

function* closeMobileSliderPageSaga() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentSliderName = urlParams.get('s');
    appHistoryPushWithSearchParams({
        s: null,
    });
    yield put(appLog({
        type: LOG_EVENT_TYPES.slider,
        payload: {
            action: 'close',
            name: currentSliderName,
        }
    }));
}

interface IAddMobileSliderPageSaga {
    payload: {
        name?: string;
    };
}

/**
 * @deprecated use addLocationSaga
 */
function* addMobileSliderPageSaga({ payload }: IAddMobileSliderPageSaga) {
    const { name, ...searchParams } = _.isObject(payload) ? payload : { name: payload };
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.append('s', name);
    const updatedSearchParams = updateURLSearchParams(urlParams, searchParams);
    appHistory.push(`${appHistory.location.pathname}?${updatedSearchParams.toString()}`);

    yield put(appLog({
        type: LOG_EVENT_TYPES.page,
        payload: {
            action: MODALS_LOG_ACTION_TYPES.OPEN,
            name,
        }
    }));
}

interface IRegistrationPrepareMobileSaga {
    payload: IRegistation
}

export function* registrationPrepareMobileSaga({ payload }: IRegistrationPrepareMobileSaga) {
    try {
        yield call(validateRegisterDataSaga, { payload });
        yield put(setRegistrationDataAction(payload));
        yield call(postRequest, '/user/register-check', payload);
        yield put(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.SIGN_UP_FORM_FILLED,
            payload: { phone_filled: !!payload.phone },
        }));
        yield put(registrationStartMobileAction(payload));
    } catch (e: any) {
        yield put(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.SIGN_UP_ERROR,
            payload: {
                error_code: e.response?.data?.messageCode || e.messageCode || 'error.something_went_wrong'
            },
        }))
        yield call(failurePut, e, registrationFailure);
    }
}

interface IRegistrationStartMobileSaga {
    payload: IRegistation
}

export function* registrationStartMobileSaga({ payload }: IRegistrationStartMobileSaga) {
    try {
        if (payload.country) {
            browserCookies.set('country', payload.country, { path: '/', maxAge: 60 * 60 * 24 * 365 });
        }
        if (!isEmailValid(payload.email)) {
            throw { messageCode: 'error.email_not_valid' };
        }
        const locale = browserCookies.get('locale');
        const data: {
            token: string;
            secret: string;
        } = yield call(postRequest, '/user/register', {
            acceptRules: payload.acceptRules,
            email: payload.email,
            phone: payload.phone,
            lang: locale,
            country: payload.country,
            password: payload.password,
            telegramInitData: payload.telegramInitData,
        });
        yield put(registrationSuccess(payload));
        yield put(setChangeEmailSuccess(false));
        yield call(updateCookiesOnAuthSaga, { payload: { token: data.token, secret: data.secret } });
        yield call(appInitStartSaga);
        yield put(logAppsflyerAction({ name: LOG_AF_EVENT_NAMES.SIGN_UP_COMPLETED }));
        yield put(pushGtmEventAction({ event: GTM_EVENTS.SIGN_UP_COMPLETED }));
        yield put(completeMobileLoginStateAction({ state: MOBILE_LOGIN_STATES.REGISTRATION_SUCCESS }));
    } catch (e: any) {
        yield call(failurePut, e, registrationFailure);
        yield put(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.SIGN_UP_ERROR,
            payload: {
                error_code: e.response?.data?.messageCode || e.messageCode || 'error.something_went_wrong'
            },
        }));
    }
}

interface IAuthRegisterMobileAppSaga {
    payload: {
        onSuccessCallback: () => void;
        onFailureCallback: () => void;
    }
}

export function* authRegisterMobileAppSaga({ payload }: IAuthRegisterMobileAppSaga) {
    try {
        browserCookies.remove('credential_id', { path: '/' });
        const options: PublicKeyCredentialCreationOptionsJSON = yield call(getRequest, '/app/auth/registration/options');
        const resp: { id: string; } = yield call(startRegistration, options);
        browserCookies.set('credential_id', resp.id, { path: '/', maxAge: 60 * 60 * 24 * 365 });
        yield call(postRequest, '/app/auth/registration/verify', resp);
        yield put(authRegisterMobileAppSuccess());
        payload.onSuccessCallback && payload.onSuccessCallback();
    } catch (e: any) {
        yield call(failurePut, e, authRegisterMobileAppFailure);
        payload.onFailureCallback && payload.onFailureCallback();
    }
}

interface IAuthVerifyMobileAppSaga {
    payload: {
        onFinallyCallback: () => void;
    }
}

export function* authVerifyMobileAppSaga({ payload }: IAuthVerifyMobileAppSaga) {
    const pin = browserCookies.get('pin');
    try {
        const isAuthenticatorAvailable: boolean = yield call(platformAuthenticatorIsAvailable);
        const token = browserCookies.get('token');
        const credentialId = browserCookies.get('credential_id');

        if (token && credentialId && isAuthenticatorAvailable) {
            const options: PublicKeyCredentialRequestOptionsJSON = yield call(getRequest, '/app/auth/authentication/options');
            const resp: {} = yield call(startAuthentication, options);
            const verification: {
                verified: boolean;
            } = yield call(postRequest, '/app/auth/authentication/verify', resp);
            if (verification.verified) {
                browserCookies.set('pinOk', pin, { path: '/', maxAge: 60 * 5 });
                yield put(logAppsflyerAction({
                    name: LOG_AF_EVENT_NAMES.LOGIN_COMPLETED,
                    payload: { method: 'fingerprint' },
                }))
            }
        }
        yield put(authVerifyMobileAppSuccess());
    } catch (e: any) {
        if (e.response?.data?.messageCode === 'error.authenticator_not_found') {
            browserCookies.remove('credential_id', { path: '/' });
        }
        yield call(failurePut, e, authVerifyMobileAppFailure);
    } finally {
        payload.onFinallyCallback && payload.onFinallyCallback();
    }
}

export function* useAbTestOptionSaga({ payload }: { payload: TAbTestNames }) {
    const abTests = yield select(getAbTestsSelector);
    const currentAbTest = abTests[payload];
    if (currentAbTest && !currentAbTest.is_used) {
        yield put(addAbTestToIsUsedQueueAction(payload));
    }
    return abTests[payload]?.option;
}

export function* mobileAppSagas() {
    //@ts-ignore
    yield takeEvery(OPEN_MOBILE_SLIDER_PAGE, openMobileSliderPageSaga);
    yield takeEvery(CLOSE_MOBILE_SLIDER_PAGE, closeMobileSliderPageSaga);
    // @ts-ignore
    yield takeEvery(ADD_MOBILE_SLIDER_PAGE, addMobileSliderPageSaga);
    //@ts-ignore
    yield takeEvery(REGISTRATION_PREPARE_MOBILE_ACTION, registrationPrepareMobileSaga);
    //@ts-ignore
    yield takeEvery(REGISTRATION_START_MOBILE_ACTION, registrationStartMobileSaga);
    //@ts-ignore
    yield takeEvery(AUTH_REGISTER_MOBILE_APP_ACTION, authRegisterMobileAppSaga);
    //@ts-ignore
    yield takeEvery(AUTH_VERIFY_MOBILE_APP_ACTION, authVerifyMobileAppSaga);
}
